import { ApolloError } from '@apollo/client';
import { parseErrorMessage } from './errorHandling';

/**
 * Client-side mirror of the server upload allowlist (BLK-3809 / PEN-2).
 *
 * The API (blockit/api, see BLK-3809) validates user-uploaded attachments
 * against a positive allowlist plus a magic-byte
 * content sniff. This list is a convenience pre-filter only — it stops users
 * from picking a file that would bounce, but the server remains the source of
 * truth (users can bypass the `accept` attribute, and the content sniff can
 * reject a file with an allowed extension). Always surface server errors too.
 *
 * KEEP IN SYNC with the API allowlist if that list changes.
 */
export const ATTACHMENT_ACCEPT_EXTENSIONS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'tif',
  'tiff',
  'bmp',
  'webp',
  'heic',
  'heif',
  'jfif',
  'dng',
  'jp2',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'txt',
  'rtf',
  'csv',
  'msg',
  'eml',
  'mp4',
  'mov',
  'pages',
];

/**
 * Value for an <input type="file"> / antd Upload `accept` attribute, e.g.
 * ".pdf,.jpg,.jpeg,...". Matching the explicit extension list is the exact
 * mirror of the server allowlist (vs. a broad `image/*`).
 */
export const ATTACHMENT_ACCEPT = ATTACHMENT_ACCEPT_EXTENSIONS.map(ext => `.${ext}`).join(',');

/**
 * Fragments of the server's attachment-rejection messages (BLK-3809). Used to
 * distinguish a file-validation rejection from a generic failure so we can
 * handle it gracefully instead of falling through to a generic error page.
 *
 * If the API copy drifts, matching simply fails and we fall back to the
 * existing generic-error behavior — never worse than before. KEEP IN SYNC.
 */
const ATTACHMENT_REJECTION_FRAGMENTS = ["file type isn't allowed", 'uploaded file is empty', 'could not be read'];

export const isAttachmentRejectionError = (error: ApolloError): boolean => {
  const messageText = parseErrorMessage(error)
    .toLowerCase()
    // Normalize typographic apostrophes to straight ones so matching is robust
    // to whichever the API renders (e.g. "isn't" vs "isn't").
    .replace(/’/g, "'");
  return ATTACHMENT_REJECTION_FRAGMENTS.some(fragment => messageText.includes(fragment));
};
